import * as React from "react";
import { IconButton, mergeStyles, Stack, Text, Toggle } from "@fluentui/react";
import { LanguageContext } from "../../utilities/LocalizationModule";
import { CopilotPromptAudience, ICopilotPromptDto } from "../../data-structures/interfaces";

type LanguageStrings = React.ContextType<typeof LanguageContext>;

export const copilotPromptRowStyle = mergeStyles({
    borderBottom: "1px solid var(--db-border)",
    padding: "8px 0",
});

export const copilotPromptMetaStyle = mergeStyles({
    color: "var(--db-fg-muted)",
});

export const copilotPromptLanguageName = (language: string | null, strings: LanguageStrings): string => {
    switch (language) {
        case "en":
            return strings.CopilotPromptLanguageEn;
        case "nl":
            return strings.CopilotPromptLanguageNl;
        case "de":
            return strings.CopilotPromptLanguageDe;
        default:
            return strings.CopilotPromptLanguageAll;
    }
};

export const copilotPromptAudienceName = (audience: CopilotPromptAudience | null, strings: LanguageStrings): string =>
    audience === 0 ? strings.CopilotPromptAudienceAll : audience === 1 ? strings.CopilotPromptAudienceAccountancy : "";

/** One line under a prompt's title, e.g. "Dutch · Accountancy tenants · Jaarwerk, Fiscaal". */
export const copilotPromptMeta = (prompt: ICopilotPromptDto, strings: LanguageStrings, showAudience: boolean): string =>
    [
        copilotPromptLanguageName(prompt.language, strings),
        showAudience ? copilotPromptAudienceName(prompt.audience, strings) : "",
        prompt.defaultLibraries.join(", "),
    ].filter(part => !!part).join(" · ");

export interface CopilotPromptListProps {
    prompts: ICopilotPromptDto[];
    /** Global prompts show their audience. */
    showAudience: boolean;
    /** A change is being saved: every control is disabled. */
    busy: boolean;
    onToggleEnabled: (prompt: ICopilotPromptDto) => void;
    /** index in `prompts`; -1 = up, 1 = down. */
    onMove: (index: number, delta: -1 | 1) => void;
    onEdit: (prompt: ICopilotPromptDto) => void;
    onDelete: (prompt: ICopilotPromptDto) => void;
}

/**
 * Editable prompt rows (the tenant's "Our prompts" and the global page): enabled toggle, move
 * up/down as in OrderedFieldsPanel, edit and delete.
 */
export const CopilotPromptList: React.FC<CopilotPromptListProps> = ({ prompts, showAudience, busy, onToggleEnabled, onMove, onEdit, onDelete }) => {
    const strings = React.useContext(LanguageContext);
    return (
        <Stack>
            {prompts.map((prompt, index) => (
                <Stack key={prompt.id} horizontal verticalAlign="center" tokens={{ childrenGap: 8 }} className={copilotPromptRowStyle}>
                    <Toggle
                        checked={prompt.isEnabled}
                        disabled={busy}
                        ariaLabel={`${strings.Enabled}: ${prompt.title}`}
                        onChange={() => onToggleEnabled(prompt)}
                        styles={{ root: { marginBottom: 0 } }}
                    />
                    <Stack.Item grow styles={{ root: { minWidth: 0 } }}>
                        <Text block>{prompt.title}</Text>
                        <Text block variant="small" className={copilotPromptMetaStyle}>{copilotPromptMeta(prompt, strings, showAudience)}</Text>
                    </Stack.Item>
                    <IconButton
                        iconProps={{ iconName: "ChevronUp" }}
                        title={strings.CopilotPromptMoveUp}
                        ariaLabel={`${strings.CopilotPromptMoveUp}: ${prompt.title}`}
                        disabled={busy || index === 0}
                        onClick={() => onMove(index, -1)}
                    />
                    <IconButton
                        iconProps={{ iconName: "ChevronDown" }}
                        title={strings.CopilotPromptMoveDown}
                        ariaLabel={`${strings.CopilotPromptMoveDown}: ${prompt.title}`}
                        disabled={busy || index === prompts.length - 1}
                        onClick={() => onMove(index, 1)}
                    />
                    <IconButton
                        iconProps={{ iconName: "Edit" }}
                        title={strings.Edit}
                        ariaLabel={`${strings.Edit}: ${prompt.title}`}
                        disabled={busy}
                        onClick={() => onEdit(prompt)}
                    />
                    <IconButton
                        iconProps={{ iconName: "Delete" }}
                        title={strings.Delete}
                        ariaLabel={`${strings.Delete}: ${prompt.title}`}
                        disabled={busy}
                        onClick={() => onDelete(prompt)}
                    />
                </Stack>
            ))}
        </Stack>
    );
};
